import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataLoader } from '@/hooks/useDataLoader';
import { Template } from '@/api/apiClient';
import { useTranslation } from '@/i18n/useTranslation';
import { Image as ImageIcon } from 'lucide-react';

export default function StylesPage() {
  const { templates, loading } = useDataLoader();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [displayTemplates, setDisplayTemplates] = useState<Template[]>([]);
  const [failedPreviews, setFailedPreviews] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (templates && templates.length > 0) {
      setDisplayTemplates(templates);
    }
  }, [templates]);

  const handleTemplateClick = (template: Template) => {
    navigate(`/create?template=${template.id}`);
  };

  const getTypeLabel = (type?: string) => {
    if (!type) return type || '-';
    const key = `sceneType.${type}` as keyof typeof t;
    const translated = t(key as any);
    return translated !== key ? translated : type;
  };

  return (
    <div className="h-full flex flex-col bg-bg-main overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-4 md:p-6 border-b border-border">
        <h1 className="text-xl md:text-2xl font-semibold text-text-primary mb-1">
          {t('styles.title')}
        </h1>
        <p className="text-sm text-text-secondary">
          {t('styles.subtitle')}
        </p>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-text-secondary">{t('loading')}</div>
          </div>
        ) : displayTemplates.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-accent" strokeWidth={2} />
              </div>
              <h2 className="text-xl font-semibold mb-2">{t('styles.noTemplates')}</h2>
              <p className="text-text-secondary">{t('styles.noTemplatesDesc')}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 auto-rows-fr">
            {displayTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template)}
                className="group relative aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-accent/50 transition-all flex flex-col bg-bg-control"
              >
                {/* Preview image or placeholder */}
                <div className="flex-1 min-h-0 flex items-center justify-center bg-bg-panel overflow-hidden relative">
                  {template.previewUrl && !failedPreviews.has(template.id) ? (
                    <img
                      src={template.previewUrl}
                      alt={template.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={() => {
                        setFailedPreviews(prev => new Set(prev).add(template.id));
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-bg-control">
                      <ImageIcon className="w-12 h-12 text-text-disabled" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                {/* Label */}
                <div className="flex-shrink-0 p-2 md:p-3 bg-bg-control border-t border-border">
                  <p className="text-xs md:text-sm font-medium text-text-primary truncate">
                    {template.name}
                  </p>
                  {template.type && (
                    <p className="text-[10px] md:text-xs text-text-secondary truncate mt-0.5">
                      {getTypeLabel(template.type)}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
